import { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Menubar } from 'primereact/menubar';
import { BlockUI } from 'primereact/blockui';
import { Panel } from 'primereact/panel';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import type { DataTableExpandedRows, DataTableFilterEvent, DataTableFilterMeta, DataTablePageEvent } from 'primereact/datatable';
import { FilterMatchMode } from 'primereact/api';
import type { Check, CustomerDeposit, JobStatus } from '../../types';
import type { PotentialCustomer } from '../../api/customers';
import type { JobProgress } from '../../hooks';

interface EditableCustomer extends PotentialCustomer {
  expanded?: boolean;
}

interface CustomerSuggestionsTabProps {
  customers: EditableCustomer[];
  checks: Check[];
  deposits: CustomerDeposit[];
  loading: boolean;
  savingSuggestionId: string | null;
  error: string | null;
  jobStatus?: JobStatus | null;
  jobProgress?: JobProgress | null;
  isIdentifyJobRunning?: boolean;
  onIdentifyCustomers: () => void;
  onAddCustomer: () => void;
  onAcceptCustomer: (suggestionId: string | null) => void;
  onRejectCustomer: (suggestionId: string | null) => void;
  onRenameCustomer: (suggestionId: string | null, name: string) => void;
  onRemoveCheck: (suggestionId: string | null, checkId: string) => void;
  onRemoveDeposit: (suggestionId: string | null, depositId: string) => void;
  onAcceptAll?: () => void;
  isAcceptingAll?: boolean;
}

export const CustomerSuggestionsTab: React.FC<CustomerSuggestionsTabProps> = ({
  customers,
  checks,
  deposits,
  loading,
  savingSuggestionId,
  error,
  jobStatus,
  jobProgress,
  isIdentifyJobRunning,
  onIdentifyCustomers,
  onAddCustomer,
  onAcceptCustomer,
  onRejectCustomer,
  onRenameCustomer,
  onRemoveCheck,
  onRemoveDeposit,
  onAcceptAll,
  isAcceptingAll,
}) => {
  const checksById = useMemo(() => {
    const map = new Map<string, Check>();
    checks.forEach((c) => map.set(c.id, c));
    return map;
  }, [checks]);

  const depositsById = useMemo(() => {
    const map = new Map<string, CustomerDeposit>();
    deposits.forEach((d) => d.id && map.set(d.id, d));
    return map;
  }, [deposits]);

  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | undefined>();
  const tableRef = useRef<DataTable<EditableCustomer[]>>(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);
  const [filteredRowCount, setFilteredRowCount] = useState<number>(customers.length);
  const [statusFilter, setStatusFilter] = useState<string[]>(['pending']);
  const [filters, setFilters] = useState<DataTableFilterMeta>(() => {
    const saved = localStorage.getItem('customerSuggestionsFilter');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          client_name: {
            value: parsed.client_name || null,
            matchMode: FilterMatchMode.CONTAINS
          },
          entity_type: {
            value: parsed.entity_type || null,
            matchMode: FilterMatchMode.EQUALS
          },
        };
      } catch {
        // If parsing fails, fall back to default
      }
    }
    return {
      client_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      entity_type: { value: null, matchMode: FilterMatchMode.EQUALS },
    };
  });

  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => statusFilter.includes(c.status));
  }, [customers, statusFilter]);

  // Track previous filtered count to detect when we need to force table refresh
  // This handles both status changes (reject) and customer additions/removals
  // The reset() call fixes PrimeReact DataTable internal state getting out of sync
  const prevFilteredCountRef = useRef(filteredCustomers.length);
  const savedFirstRef = useRef(first);
  useEffect(() => {
    savedFirstRef.current = first;
  }, [first]);

  useEffect(() => {
    if (prevFilteredCountRef.current !== filteredCustomers.length) {
      prevFilteredCountRef.current = filteredCustomers.length;
      const savedFirst = savedFirstRef.current;
      // Use setTimeout to let React finish its render cycle before resetting
      setTimeout(() => {
        tableRef.current?.reset();
        // Restore pagination after reset if we were beyond the first page
        if (savedFirst > 0 && savedFirst < filteredCustomers.length) {
          setFirst(savedFirst);
        } else if (savedFirst >= filteredCustomers.length) {
          // If we were on a page that no longer exists, go to last valid page
          setFirst(Math.max(0, Math.floor((filteredCustomers.length - 1) / rows) * rows));
        }
      }, 0);
    }
  }, [filteredCustomers.length, rows]);

  const pendingCount = useMemo(() => {
    return customers.filter((c) => c.status === 'pending').length;
  }, [customers]);

  useEffect(() => {
    if (!expandedRows) return;

    const currentCustomerIds = new Set(
      customers.filter(c => c.suggestion_id != null).map(c => String(c.suggestion_id))
    );

    const cleanedRows: DataTableExpandedRows = {};
    let hasChanges = false;

    Object.keys(expandedRows).forEach(id => {
      if (currentCustomerIds.has(id)) {
        cleanedRows[id] = true;
      } else {
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setExpandedRows(Object.keys(cleanedRows).length > 0 ? cleanedRows : undefined);
    }
  }, [customers, expandedRows]);

  useEffect(() => {
    setFilteredRowCount(filteredCustomers.length);
  }, [filteredCustomers]);

  useEffect(() => {
    const clientNameFilter = filters.client_name;
    const entityTypeFilter = filters.entity_type;
    const clientNameValue = clientNameFilter && 'value' in clientNameFilter ? clientNameFilter.value : null;
    const entityTypeValue = entityTypeFilter && 'value' in entityTypeFilter ? entityTypeFilter.value : null;
    localStorage.setItem('customerSuggestionsFilter', JSON.stringify({
      client_name: clientNameValue,
      entity_type: entityTypeValue
    }));
  }, [filters]);

  const onFilter = (e: DataTableFilterEvent) => {
    const filtered = e.filteredValue as EditableCustomer[] | null;
    const newCount = filtered ? filtered.length : filteredCustomers.length;
    setFilteredRowCount(newCount);
  };

  const onPage = (e: DataTablePageEvent) => {
    setFirst(e.first);
    setRows(e.rows);
    setTimeout(() => {
      const wrapper = tableRef.current?.getElement()?.querySelector('.p-datatable-wrapper');
      if (wrapper) {
        wrapper.scrollTop = 0;
      }
    }, 0);
  };

  const rowClassName = (customer: EditableCustomer) => ({
    [`status-${customer.status}`]: true,
  });

  const nameEditor = (options: any) => {
    return (
      <InputText
        type="text"
        value={options.value}
        onChange={(e) => options.editorCallback(e.target.value)}
      />
    );
  };

  const onCellEditComplete = (e: any) => {
    const { rowData, newValue, field } = e;
    if (field === 'client_name' && newValue && newValue.trim() !== '') {
      onRenameCustomer(rowData.suggestion_id, newValue);
      // Force table to refresh after edit to avoid stale internal state
      setTimeout(() => {
        tableRef.current?.reset();
      }, 0);
    }
  };

  const customerNameBodyTemplate = (customer: EditableCustomer) => (
    <span>{customer.client_name}</span>
  );

  const entityTypeOptions = [
    { label: 'Person', value: 'p' },
    { label: 'Company', value: 'c' },
  ];

  const entityTypeBodyTemplate = (customer: EditableCustomer) => {
    if (customer.entity_type === 'c') {
      return <Tag value="Company" severity="warning" />;
    } else if (customer.entity_type === 'p') {
      return <Tag value="Person" severity="info" />;
    }
    return <span>-</span>;
  };

  const entityTypeFilterElement = (options: any) => (
    <Dropdown
      value={options.value}
      options={entityTypeOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="All"
      showClear
      className="p-column-filter"
      style={{ minWidth: '8rem' }}
    />
  );

  const summaryBodyTemplate = (customer: EditableCustomer) => (
    <span className="summary-count">
      {customer.referenced_checks.length}{' '}
      {customer.referenced_checks.length === 1 ? 'check' : 'checks'}
      {customer.referenced_deposits.length > 0 && (
        <>
          , {customer.referenced_deposits.length}{' '}
          {customer.referenced_deposits.length === 1 ? 'deposit' : 'deposits'}
        </>
      )}
    </span>
  );

  const actionsBodyTemplate = (customer: EditableCustomer) => (
    <div className="row-actions">
      <Button
        icon="ms-out ms-out-check"
        link
        tooltip="Accept customer"
        tooltipOptions={{ position: 'bottom' }}
        onClick={() => onAcceptCustomer(customer.suggestion_id)}
        disabled={savingSuggestionId === customer.suggestion_id}
      />
      <Button
        icon="ms-out ms-out-close"
        link
        tooltip="Reject customer"
        tooltipOptions={{ position: 'bottom' }}
        onClick={() => onRejectCustomer(customer.suggestion_id)}
      />
    </div>
  );

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return '-';
    const numeric = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(numeric)) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numeric);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
    } catch {
      return dateStr;
    }
  };

  const getCustomerChecks = (customer: EditableCustomer) => {
    return customer.referenced_checks
      .map((id) => checksById.get(id))
      .filter((c): c is Check => Boolean(c));
  };

  const getCustomerDeposits = (customer: EditableCustomer) => {
    return customer.referenced_deposits
      .map((id) => depositsById.get(id))
      .filter((d): d is CustomerDeposit => Boolean(d));
  };

  const checkRemoveBodyTemplate = (customer: EditableCustomer) => (rowData: Check) => (
    <div className="row-actions">
      <Button
        icon="ms-out ms-out-close"
        link
        tooltip="Remove check"
        onClick={() => onRemoveCheck(customer.suggestion_id, rowData.id)}
      />
    </div>
  );

  const depositRemoveBodyTemplate = (customer: EditableCustomer) => (rowData: CustomerDeposit) => (
    <div className="row-actions">
      <Button
        icon="ms-out ms-out-close"
        link
        tooltip="Remove deposit"
        onClick={() => onRemoveDeposit(customer.suggestion_id, rowData.id)}
      />
    </div>
  );

  const rowExpansionTemplate = (customer: EditableCustomer) => {
    const customerChecks = getCustomerChecks(customer);
    const customerDeposits = getCustomerDeposits(customer);

    return (
      <div className="row-expansion-container">
        {customerChecks.length > 0 && (
          <Panel header="Checks">
            <DataTable value={customerChecks} size="small">
              <Column field="account_name" header="Account" style={{ width: '20%' }} />
              <Column field="memo" header="Memo" style={{ width: '15%' }} />
              <Column field="payee_name" header="Payee" style={{ width: '20%' }} />
              <Column field="check_number" header="Check #" style={{ width: '10%' }} />
              <Column
                field="check_date"
                header="Date"
                style={{ width: '10%' }}
                body={(rowData: Check) => formatDate(rowData.check_date)}
              />
              <Column
                field="amount"
                header="Amount"
                style={{ width: '10%' }}
                body={(rowData: Check) => formatCurrency(rowData.amount)}
              />
              <Column body={checkRemoveBodyTemplate(customer)} style={{ width: '8%' }} />
            </DataTable>
          </Panel>
        )}
        {customerDeposits.length > 0 && (
          <Panel header="Deposits">
            <DataTable value={customerDeposits} size="small">
              <Column field="account_name" header="Account" style={{ width: '20%' }} />
              <Column field="customer_name" header="Customer" style={{ width: '20%' }} />
              <Column
                field="deposit_date"
                header="Date"
                style={{ width: '10%' }}
                body={(rowData: CustomerDeposit) => formatDate(rowData.deposit_date)}
              />
              <Column
                field="amount"
                header="Amount"
                style={{ width: '10%' }}
                body={(rowData: CustomerDeposit) => formatCurrency(rowData.amount)}
              />
              <Column field="status" header="Status" style={{ width: '12%' }} />
              <Column body={depositRemoveBodyTemplate(customer)} style={{ width: '8%' }} />
            </DataTable>
          </Panel>
        )}
        {customerChecks.length === 0 && customerDeposits.length === 0 && (
          <Message severity="info" text="No checks or deposits assigned to this customer." />
        )}
      </div>
    );
  };

  const handleExpandAll = () => {
    const allExpanded: DataTableExpandedRows = {};
    filteredCustomers.forEach((c) => {
      if (c.suggestion_id != null) {
        allExpanded[String(c.suggestion_id)] = true;
      }
    });
    setExpandedRows(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedRows(undefined);
  };

  const handleToggleStatus = (status: string) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const allExpanded = useMemo(() => {
    if (!expandedRows) return false;
    const expandedCount = Object.keys(expandedRows).length;
    const customersWithIds = filteredCustomers.filter(c => c.suggestion_id != null).length;
    return expandedCount === customersWithIds && customersWithIds > 0;
  }, [expandedRows, filteredCustomers]);

  const menubarItems = useMemo(
    () => [
      {
        label: isIdentifyJobRunning ? 'Identifying...' : 'Make Suggestions',
        icon: isIdentifyJobRunning ? 'pi pi-spin pi-spinner' : 'ms-out ms-out-bolt',
        command: onIdentifyCustomers,
        disabled: loading || isIdentifyJobRunning,
      },
      {
        label: 'Add Customer',
        icon: 'ms-out ms-out-add',
        command: onAddCustomer,
        disabled: loading,
      },
      {
        label: isAcceptingAll ? 'Accepting...' : `Accept All (${pendingCount})`,
        icon: isAcceptingAll ? 'pi pi-spin pi-spinner' : 'ms-out ms-out-check-circle',
        command: onAcceptAll,
        disabled: loading || pendingCount === 0 || !onAcceptAll || isAcceptingAll,
      },
      {
        label: 'Status',
        icon: 'ms-out ms-out-visibility',
        items: [
          {
            label: statusFilter.includes('pending') ? 'Hide Pending' : 'Show Pending',
            command: () => handleToggleStatus('pending'),
          },
          {
            label: statusFilter.includes('accepted') ? 'Hide Accepted' : 'Show Accepted',
            command: () => handleToggleStatus('accepted'),
          },
          {
            label: statusFilter.includes('rejected') ? 'Hide Rejected' : 'Show Rejected',
            command: () => handleToggleStatus('rejected'),
          },
        ],
      },
      {
        label: allExpanded ? 'Collapse All' : 'Expand All',
        icon: allExpanded ? 'ms-out ms-out-expand-less' : 'ms-out ms-out-expand-more',
        command: allExpanded ? handleCollapseAll : handleExpandAll,
        disabled: loading || filteredCustomers.length === 0,
      },
    ],
    [loading, isIdentifyJobRunning, onIdentifyCustomers, onAddCustomer, onAcceptAll, pendingCount, allExpanded, filteredCustomers.length, statusFilter, isAcceptingAll]
  );

  return (
    <>
      {error && <Message severity="error" text={error} />}
      <Menubar
        model={menubarItems}
        end={
          <span className="filter-count">
            <span className="filter-container"><strong>{deposits.length}</strong> deposits</span>
            <span className="filter-container"><strong>{checks.length}</strong> checks</span>
          </span>
        }
        className="actions-menubar"
      />

      {(jobStatus === 'pending' || jobStatus === 'running') && (
        <div className="upload-progress">
          <ProgressBar
            mode={jobProgress?.total ? 'determinate' : 'indeterminate'}
            value={jobProgress?.total ? Math.round((jobProgress.current / jobProgress.total) * 100) : undefined}
          />
          <span className="upload-message">
            {jobStatus === 'pending' && 'Waiting for other job to complete...'}
            {jobStatus === 'running' && (jobProgress?.message || 'Processing...')}
          </span>
        </div>
      )}

      <BlockUI blocked={isAcceptingAll}>
        <DataTable
        ref={tableRef}
        value={filteredCustomers}
        dataKey="suggestion_id"
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
        rowExpansionTemplate={rowExpansionTemplate}
        rowClassName={rowClassName}
        size="small"
        scrollable
        scrollHeight="calc(100vh - 200px)"
        className="customer-suggestions-table"
        paginator
        first={first}
        rows={rows}
        emptyMessage="No customer suggestions found"
        rowsPerPageOptions={[25, 50, 100]}
        filterDisplay="menu"
        filters={filters}
        onFilter={(e) => {
          setFilters(e.filters);
          onFilter(e);
        }}
        onPage={onPage}
        totalRecords={filteredRowCount}
        editMode="cell"
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> customers
            </span>
          )
        }}
      >
          <Column expander style={{ width: '3%' }} />
          <Column
            field="entity_type"
            header="Type"
            style={{ width: '5%' }}
            body={entityTypeBodyTemplate}
            filter
            sortable
            filterElement={entityTypeFilterElement}
            showFilterMatchModes={false}
          />          
          <Column
            field="client_name"
            header="Customer"
            style={{ width: '35%' }}
            body={customerNameBodyTemplate}
            editor={nameEditor}
            onCellEditComplete={onCellEditComplete}
            filter
            sortable
            filterPlaceholder="Search by customer name"
            showFilterMatchModes={false}
          />

          <Column
            header="Summary"
            style={{ width: '25%' }}
            body={summaryBodyTemplate}
          />
          <Column body={actionsBodyTemplate}/>
        </DataTable>
      </BlockUI>
    </>
  );
};

export default CustomerSuggestionsTab;
